import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { MonitorSmartphone, ExternalLink, UserPlus, RefreshCw, Eye, History } from 'lucide-react';
import type {
  AcceptRemoteInviteRequest,
  CreateRemoteSessionResponse,
  Entitlements,
  PortalAppliance,
  RemoteApplianceSummary,
  RemoteInvitePreview,
  RemoteViewActivity,
} from '@scada-ink/shared/cloud';
import { Button, Container } from '@/components/ui';
import { Alert, TextField } from '@/components/form';
import { usePageTitle } from '@/components/layout/SiteLayout';
import { cloudApi, errorMessage } from '@/lib/cloudApi';
import { useSession } from '@/lib/session';
import { useAsync } from '@/lib/useAsync';
import { Card, ConnectionBadge, EmptyState, LoadError, Loading, PortalHeader, Table, formatDateTime, timeAgo } from './components';
import { Coverage } from './ServicesPages';

// ── Open an appliance (everyone, viewers included) ─────────────────────────

function ApplianceLauncher() {
  const { data, error, loading, reload } = useAsync(() => cloudApi.get<RemoteApplianceSummary[]>('/api/portal/remote/appliances'));
  const [opening, setOpening] = useState<string | null>(null);
  const [launchError, setLaunchError] = useState<string | null>(null);

  async function open(a: RemoteApplianceSummary) {
    setOpening(a.id);
    setLaunchError(null);
    // Open the tab synchronously so the browser doesn't treat it as a pop-up.
    const tab = window.open('about:blank', '_blank');
    try {
      const { url } = await cloudApi.post<CreateRemoteSessionResponse>('/api/portal/remote/sessions', { applianceId: a.id });
      if (tab) tab.location.href = url;
      else window.location.assign(url);
    } catch (err) {
      tab?.close();
      setLaunchError(errorMessage(err));
    } finally {
      setOpening(null);
    }
  }

  return (
    <Card
      title="Open an appliance"
      actions={
        <button type="button" onClick={() => void reload()} className="inline-flex items-center gap-1 text-xs font-semibold text-brand-primary">
          <RefreshCw size={12} /> Refresh
        </button>
      }
      padded={false}
    >
      {launchError && (
        <div className="px-5 pt-4">
          <Alert kind="error">{launchError}</Alert>
        </div>
      )}
      {loading && !data ? (
        <Loading />
      ) : error || !data ? (
        <LoadError message={error ?? 'Could not load appliances.'} onRetry={reload} />
      ) : data.length === 0 ? (
        <EmptyState icon={MonitorSmartphone} title="Nothing to view yet" body="Appliances appear here once remote viewing is switched on for them." />
      ) : (
        <ul className="divide-y divide-glass-border">
          {data.map((a) => (
            <li key={a.id} className="flex flex-col sm:flex-row sm:items-center gap-3 px-5 py-4">
              <div className="flex-1 min-w-0">
                <div className="font-semibold">{a.name}</div>
                <div className="text-xs text-text-muted">
                  {a.site || 'No site'} · {a.tunnel === 'connected' ? `connected ${timeAgo(a.tunnelConnectedAt)}` : `last report ${timeAgo(a.lastSeenAt)}`}
                </div>
                {!a.viewable && a.reason && <div className="text-xs text-status-idle mt-0.5">{a.reason}</div>}
              </div>
              <div className="flex items-center gap-3">
                <span
                  className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold ${
                    a.tunnel === 'connected' ? 'bg-status-running/10 text-status-running' : 'bg-ink-muted text-text-muted'
                  }`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${a.tunnel === 'connected' ? 'bg-status-running' : 'bg-status-offline'}`} />
                  {a.tunnel === 'connected' ? 'Ready to view' : 'Not connected'}
                </span>
                <Button size="sm" disabled={!a.viewable || opening === a.id} onClick={() => void open(a)}>
                  <ExternalLink size={13} /> {opening === a.id ? 'Opening…' : 'Open'}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
      <p className="px-5 py-3 text-xs text-text-muted border-t border-glass-border">
        Opens in a new tab, read-only. Viewing one appliance at a time per browser — opening another switches the view.
      </p>
    </Card>
  );
}

// ── Viewers (owner) ────────────────────────────────────────────────────────

function Viewers({ limit }: { limit: number | null }) {
  return (
    <Card title="Viewers" actions={<span className="text-xs text-text-secondary">{limit === null ? 'Unlimited' : `${limit}`} Remote View seats</span>}>
      <p className="text-sm text-text-secondary -mt-1 mb-4">
        Invite people, and choose which appliances and screens each one can open, on the App users page. Remote access is managed there, not on the appliance.
      </p>
      <Button to="/portal/app-users" size="sm" variant="secondary">
        <UserPlus size={13} /> Manage app users
      </Button>
    </Card>
  );
}

function Activity() {
  const { data, error, loading, reload } = useAsync(() => cloudApi.get<RemoteViewActivity[]>('/api/portal/remote/activity?limit=50'));
  const endLabel: Record<string, string> = { signed_out: 'Signed out', expired: 'Expired', viewer_removed: 'Viewer removed', access_lost: 'Access ended' };
  return (
    <Card
      title={
        <span className="inline-flex items-center gap-2">
          <History size={15} /> Viewing activity
        </span>
      }
      padded={false}
    >
      {loading && !data ? (
        <Loading />
      ) : error || !data ? (
        <LoadError message={error ?? 'Could not load activity.'} onRetry={reload} />
      ) : data.length === 0 ? (
        <EmptyState icon={Eye} title="No remote views yet" body="Every remote viewing session is recorded here: who, which appliance, and when." />
      ) : (
        <Table head={['Started', 'Person', 'Appliance', 'Duration', 'Requests', 'Ended']}>
          {data.map((a) => {
            const end = a.endedAt ?? a.lastSeenAt;
            const minutes = Math.max(1, Math.round((new Date(end).getTime() - new Date(a.startedAt).getTime()) / 60000));
            return (
              <tr key={a.id}>
                <td className="px-5 py-2.5 whitespace-nowrap text-text-secondary">{formatDateTime(a.startedAt)}</td>
                <td className="px-5 py-2.5">
                  <div className="font-medium">{a.userName || a.userEmail}</div>
                  <div className="text-xs text-text-muted">
                    {a.userEmail} · {a.userRole}
                  </div>
                </td>
                <td className="px-5 py-2.5">{a.applianceName}</td>
                <td className="px-5 py-2.5 tabular-nums">{minutes} min</td>
                <td className="px-5 py-2.5 tabular-nums">{a.requests.toLocaleString()}</td>
                <td className="px-5 py-2.5 whitespace-nowrap">
                  {a.endedAt ? <span className="text-text-secondary">{endLabel[a.endReason ?? ''] ?? 'Ended'}</span> : <span className="text-status-running font-semibold">Active</span>}
                </td>
              </tr>
            );
          })}
        </Table>
      )}
    </Card>
  );
}

export function RemoteViewingPage() {
  usePageTitle('Remote Viewing');
  const { session } = useSession();
  const isOwner = session?.user.role === 'owner';
  const isViewer = session?.user.role === 'viewer';
  const service = useAsync(async () => {
    if (isViewer) return null;
    const [appliances, entitlements] = await Promise.all([cloudApi.get<PortalAppliance[]>('/api/portal/appliances'), cloudApi.get<Entitlements>('/api/portal/entitlements')]);
    return { appliances, entitlements };
  }, [isViewer]);

  const remote = service.data?.entitlements.remote;

  return (
    <>
      <PortalHeader
        title="Remote Viewing"
        description={
          isViewer
            ? `Read-only access to ${session?.account.company}'s dashboards, custom pages and reports.`
            : 'See dashboards, custom pages and reports from anywhere, read-only. The appliance connects out to Scada.Ink Cloud — no inbound firewall ports.'
        }
        actions={
          !isViewer &&
          remote && (
            <Button to={`/portal/billing?plan=${remote.active ? 'remote-view-plus' : 'remote-view'}`} size="sm" variant={remote.active ? 'secondary' : 'primary'}>
              <MonitorSmartphone size={14} /> {remote.active ? 'More viewers' : 'Subscribe'}
            </Button>
          )
        }
      />

      {!isViewer && remote && !remote.active && (
        <div className="mb-6">
          <Alert kind="info">No remote viewing plan is active. Subscribe to Remote View or Cloud Complete to switch it on for your appliances.</Alert>
        </div>
      )}

      <div className="space-y-6">
        <ApplianceLauncher />
        {!isViewer && (
          <div className="grid lg:grid-cols-2 gap-6">
            {service.loading && !service.data ? (
              <Loading />
            ) : service.error || !service.data ? (
              <LoadError message={service.error ?? 'Could not load remote viewing settings.'} onRetry={service.reload} />
            ) : (
              <Coverage kind="remote" appliances={service.data.appliances} covered={service.data.entitlements.remote.appliances} onChanged={() => void service.reload()} />
            )}
            {isOwner && remote?.active ? (
              <Viewers limit={remote.viewers} />
            ) : (
              <Card title="Viewers">
                <p className="text-sm text-text-muted">{isOwner ? 'Available once a Remote View plan is active.' : 'Only the account owner can invite viewers.'}</p>
              </Card>
            )}
          </div>
        )}
        {isOwner && <Activity />}
      </div>

      {!isViewer && (
        <p className="mt-6 text-xs text-text-muted">
          The appliance needs Cloud health reporting turned on (Appliance → System) and outbound HTTPS to cloud.scada.ink. <Link to="/portal/appliances" className="text-brand-primary font-semibold">Appliances</Link>
        </p>
      )}
    </>
  );
}

// ── Accept an invitation (public) ──────────────────────────────────────────

export function RemoteInvitePage() {
  usePageTitle('Accept invitation');
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const { refresh } = useSession();
  const preview = useAsync(() => cloudApi.get<RemoteInvitePreview>(`/api/portal/remote/invite?token=${encodeURIComponent(token)}`), [token]);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function accept(e: FormEvent) {
    e.preventDefault();
    if (password.length < 10) return setError('Use a password of at least 10 characters.');
    setBusy(true);
    setError(null);
    try {
      const body: AcceptRemoteInviteRequest = { token, password, name: name.trim() || undefined };
      await cloudApi.post('/api/portal/remote/invite/accept', body);
      await refresh();
      navigate('/portal/remote', { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Container className="py-16 lg:py-24">
      <div className="max-w-md mx-auto">
        <div className="glass-panel p-7">
          <span className="w-11 h-11 rounded-xl bg-brand-primary/10 text-brand-primary flex items-center justify-center">
            <MonitorSmartphone size={22} />
          </span>
          {preview.loading && !preview.data ? (
            <Loading />
          ) : preview.error || !preview.data ? (
            <>
              <h1 className="mt-4 text-2xl font-bold">This invitation can’t be used</h1>
              <p className="mt-2 text-sm text-text-secondary">{preview.error ?? 'It may have expired or already been accepted.'} Ask the person who invited you to send a new one.</p>
              <div className="mt-6">
                <Button to="/login" variant="secondary">
                  Sign in instead
                </Button>
              </div>
            </>
          ) : (
            <>
              <h1 className="mt-4 text-2xl font-bold">View {preview.data.company}’s appliances</h1>
              <p className="mt-2 text-sm text-text-secondary">
                {preview.data.invitedBy ? `${preview.data.invitedBy} invited you` : 'You were invited'} to remotely view dashboards, custom pages and reports, read-only. Set a password to accept.
              </p>
              <form onSubmit={accept} className="mt-6 space-y-4">
                <TextField label="Email" value={preview.data.email} disabled />
                <TextField label="Your name" value={name} placeholder={preview.data.name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
                <TextField label="Password" type="password" required minLength={10} autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} hint="At least 10 characters." />
                {error && <Alert kind="error">{error}</Alert>}
                <Button type="submit" size="lg" className="w-full" disabled={busy}>
                  {busy ? 'Accepting…' : 'Accept and continue'}
                </Button>
                <p className="text-xs text-text-muted text-center">Invitation expires {formatDateTime(preview.data.expiresAt)}.</p>
              </form>
            </>
          )}
        </div>
      </div>
    </Container>
  );
}

export { ConnectionBadge };
